import { oc } from "@orpc/contract";
import { z } from "zod";

const configSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  mailboxAddress: z.email(),
  searchQuery: z.string(),
  lookbackHours: z.number(),
  enabled: z.boolean(),
  serviceAccountEmail: z.string(),
  lastPolledAt: z.date().nullable(),
  lastError: z.string().nullable(),
  pendingDeliveries: z.number().int().nonnegative(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

const settingsSchema = z.object({
  name: z.string().trim().min(1).max(255),
  mailboxAddress: z.string().trim().toLowerCase().pipe(z.email()),
  searchQuery: z.string().trim().max(1024),
  lookbackHours: z.number().int().min(1).max(168),
});

const idSchema = z.object({ id: z.uuid() });
const credentialsSchema = z.string().min(1).max(16384);

export const gmailIngestContracts = {
  list: oc.output(z.array(configSchema)).route({
    method: "GET",
    summary: "List Gmail ingest mailboxes",
    description:
      "Returns the organization's Gmail ingest mailboxes with poll status and pending delivery counts. Never returns credentials.",
  }),
  create: oc
    .input(
      settingsSchema.extend({
        searchQuery: settingsSchema.shape.searchQuery.default(""),
        lookbackHours: settingsSchema.shape.lookbackHours.default(24),
        serviceAccountSecretJson: credentialsSchema,
      }),
    )
    .output(configSchema)
    .route({
      summary: "Create a disabled Google Workspace ingest mailbox",
      description:
        "Saves a Gmail mailbox registration in the disabled state and encrypts its service account key.",
    }),
  update: oc
    .input(
      settingsSchema.omit({ mailboxAddress: true }).partial().extend({
        id: z.uuid(),
        enabled: z.boolean().optional(),
        serviceAccountSecretJson: credentialsSchema.optional(),
      }),
    )
    .output(configSchema)
    .route({
      summary: "Update or enable a Gmail ingest mailbox",
      description:
        "Changes a mailbox's settings or key. Enabling first verifies delegated access to the configured mailbox.",
    }),
  delete: oc
    .input(idSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Remove a Gmail ingest mailbox",
      description:
        "Stops ingestion for the mailbox. Previously ingested emails remain.",
    }),
  testConnection: oc
    .input(idSchema)
    .output(z.object({ mailboxAddress: z.email() }))
    .route({
      summary: "Verify delegated read access to the Gmail mailbox",
      description:
        "Calls Gmail as the mailbox through domain-wide delegation and returns the address Google reports.",
    }),
  sync: oc
    .input(
      idSchema.extend({
        lookbackHours: z.number().int().min(1).max(168).optional(),
      }),
    )
    .output(
      z.object({
        status: z.enum(["completed", "busy", "disabled"]),
        discovered: z.number().int().nonnegative(),
      }),
    )
    .route({
      summary: "Poll Gmail now or backfill up to seven days",
      description:
        "Discovers new messages immediately, optionally reaching back up to 168 hours, and queues them for ingestion.",
    }),
};
